import { QueryClient, infiniteQueryOptions } from "@tanstack/react-query";
import { getFeaturedProducts, type FeaturedProductsParams, type FeaturedProductsResponse } from "@wtf/shared/api/featured";
import type { ProductDto } from "@wtf/shared";

// Helpers local to featured products
let featuredProductsSessionSeed: string | undefined;

export function getFeaturedProductsSessionSeed(userProvidedSeed?: string): string {
  if (userProvidedSeed && userProvidedSeed.length > 0) return userProvidedSeed;
  if (!featuredProductsSessionSeed) {
    featuredProductsSessionSeed = `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  return featuredProductsSessionSeed;
}

export function buildFeaturedProductsInfiniteKey(params: Omit<FeaturedProductsParams, "offset"> & { seed: string }) {
  return ["featured", "products", "infinite", params] as const;
}

export function cacheProductsIndividually(queryClient: QueryClient, products: ProductDto[]): void {
  products.forEach((product) => {
    queryClient.setQueryData(["product", product.id], product);
  });
}

// Infinite options with seed and duplicate prevention
export const featuredProductsInfiniteOptions = (
  queryClient: QueryClient,
  params: Omit<FeaturedProductsParams, "offset"> = {}
) => {
  const seed = getFeaturedProductsSessionSeed(params.seed);
  const key = buildFeaturedProductsInfiniteKey({ ...params, seed });

  return infiniteQueryOptions<FeaturedProductsResponse, Error>({
    queryKey: key,
    queryFn: async (context) => {
      const offset = context.pageParam as number;

      const requestParams = {
        limit: 30,
        ...params,
        offset,
        seed,
      };

      const result = await getFeaturedProducts(requestParams);
      cacheProductsIndividually(queryClient, result.items);
      return result;
    },
    getNextPageParam: (lastPage: FeaturedProductsResponse): number | undefined => {
      return lastPage.hasMore ? lastPage.nextOffset : undefined;
    },
    initialPageParam: 0,
    staleTime: 20 * 60 * 1000, // 20 minutes
    // Optimize for performance - featured content doesn't change often
    gcTime: 4 * 60 * 60 * 1000, // 4 hours - prevent GC during extended away periods
    refetchOnWindowFocus: false, // Prevent refetch storms on tab resume
    refetchOnMount: false, // Products are pre-cached by the query
  });
};
